import { type UseQueryResult, useQuery } from '@tanstack/react-query';

import { type IAudit, findAllById } from '@ifs-gcp/core';
import { isDocumentUploadChild } from '@ifs-gcp/core/helpers';

import api from '@/api/axios';
import i18n from '@/app/config/i18n';
import { queryClient } from '@/app/config/react-query';
import type { DocumentDefinitionsResponse } from '@/types/documentUpload';

const documentDefinitionsQuery = (
  auditCaseUuid: string,
  moduleId: string,
  language: string,
  audit: IAudit,
) => ({
  queryKey: ['audit-case-document-definitions', auditCaseUuid, moduleId, language] as const,
  queryFn: async () => {
    const { data } = await api.get<DocumentDefinitionsResponse>(
      `/audit-cases/${auditCaseUuid}/documents/document-definitions`,
      { params: { moduleId } },
    );

    audit.documentUploadStore.setDocumentConfigs([moduleId], data);

    return data;
  },
});

export const useGetAuditCaseDocumentDefinitions = (
  auditCaseUuid: string,
  moduleId: string,
  language: string,
  audit: IAudit,
): UseQueryResult<DocumentDefinitionsResponse, Error> => {
  return useQuery(documentDefinitionsQuery(auditCaseUuid, moduleId, language, audit));
};

function getDocumentModuleIds(audit: IAudit): string[] {
  return [
    ...new Set(
      findAllById(audit.templateHandler.schema, 'widgetDocumentUpload')
        .filter(isDocumentUploadChild)
        .flatMap((node) => node.props.context),
    ),
  ];
}

export async function prefetchAllDocumentDefinitions(
  auditCaseUuid: string,
  audit: IAudit,
): Promise<void> {
  const moduleIds = getDocumentModuleIds(audit);
  await Promise.all(
    moduleIds.map((moduleId) =>
      queryClient.fetchQuery(
        documentDefinitionsQuery(auditCaseUuid, moduleId, i18n.language || 'en', audit),
      ),
    ),
  );
}
